/**
 * defaultCommunityLanding — DEV-1448, the ONE default-community resolver
 * (Skip ruling 2026-07-09, POLICY_NAVIGATION_RAILS §7.6).
 *
 * Every entry point that answers "which community view does this user land
 * on?" — the post-authentication landing ("/") and the My Impact nav-item
 * click — calls THIS function. Resolution order:
 *
 *   1. Explicit pref (server-backed security_users.default_community_view;
 *      'overview' = the Communities browse page, else a community guid).
 *   2. The inviting organization (onboarding state's invitedCommunityGuid,
 *      resolved server-side from invitation_guid → invitations.community_guid).
 *   3. Home geo community via the existing landing walk (declared →
 *      nearest-active-ancestor; the only path for future self-setup users).
 *
 * STALE prefs fall through, never crash, never blank-pane: a guid that no
 * longer exists in the loaded entity set (community deleted / unresolvable)
 * skips to the next step. A guid that exists but is INACTIVE is corrected
 * by the sanctioned landing walk (nearest active ancestor) rather than
 * discarded — same dormant-membership semantics as DEV-1413.
 *
 * Membership is deliberately NOT checked ("community left"): the pref may
 * point at any visible community (a geo place has no membership concept),
 * and the community page itself handles visibility. Flagged to the
 * architect in the DEV-1448 status update.
 *
 * Returns a ROUTE string so callers can't diverge on URL shape.
 */
import {
  createLandingResolver,
  type LandingEntityLike,
  type LandingRelationshipLike,
} from './landingResolver';

export interface DefaultCommunityLandingInputs {
  /** Explicit pref: null (unset) | 'overview' | community guid. */
  pref: string | null | undefined;
  /** The inviting org community from onboarding state (step 2). */
  invitedCommunityGuid?: string | null;
  /** The user's home community, ALREADY landing-walked (App.tsx memo). */
  homeCommunityGuid?: string | null;
  entities: LandingEntityLike[] | undefined;
  relationships: LandingRelationshipLike[] | undefined;
}

/** Route for the Communities browse page (the 'overview' sentinel target). */
export const COMMUNITIES_BROWSE_ROUTE = '/communities';

/**
 * §7.9 Start Page — the fixed-surface option routes (LOCKED set §7.9.4).
 * 'feed' is deliberately absent: it means "my default community's feed",
 * which is the same resolution the NULL fallthrough chain performs.
 */
export const START_PAGE_SURFACE_ROUTES: Record<string, string> = {
  action_center: '/actions',
  my_impact: '/memberships',
  discover: '/discover',
  overview: COMMUNITIES_BROWSE_ROUTE,
};

export interface StartPageResolution {
  route: string;
  /**
   * §7.9.9 — set when the explicit community pref could not land AS SAVED:
   * the guid is unknown (community gone) or the landing walk corrected it
   * to an ancestor (community dissolved/inactive). The caller shows the
   * one-time "your start page pointed at X…" notice; never silent.
   */
  stalePrefGuid: string | null;
}

export function resolveStartPage(
  inputs: DefaultCommunityLandingInputs,
): StartPageResolution {
  const { pref, invitedCommunityGuid, homeCommunityGuid, entities, relationships } = inputs;

  // Known-entity set: the staleness gate. The landing walk alone can't be
  // it — an UNKNOWN guid walks to the top active root instead of failing,
  // which would mask a stale pref rather than falling through to step 2.
  const known = new Set<string>();
  for (const e of entities || []) {
    const g = String(e?.entity_guid || '').toUpperCase();
    if (g) known.add(g);
  }
  const resolve = createLandingResolver(entities, relationships);

  /** Guid → landed guid when it survives staleness + the walk, else null. */
  const landFor = (guid: string | null | undefined): string | null => {
    const g = String(guid || '').trim();
    if (!g || !known.has(g.toUpperCase())) return null; // stale/unknown → fall through
    return resolve(g) || null; // inactive → nearest active ancestor
  };

  // 1. Explicit pref — surface keys route directly (§7.9.4); 'feed' and
  //    unset fall through to the community chain, which IS the feed
  //    landing (§7.8 item 1 — the route renders the community standalone).
  const prefTrim = String(pref || '').trim();
  const prefLower = prefTrim.toLowerCase();
  if (prefLower in START_PAGE_SURFACE_ROUTES) {
    return { route: START_PAGE_SURFACE_ROUTES[prefLower], stalePrefGuid: null };
  }

  const prefIsCommunity = !!prefTrim && prefLower !== 'feed';
  let stalePrefGuid: string | null = null;
  if (prefIsCommunity) {
    const landed = landFor(prefTrim);
    if (landed && landed.toUpperCase() === prefTrim.toUpperCase()) {
      return { route: `/communities/${landed}`, stalePrefGuid: null };
    }
    if (landed) {
      // Exists but inactive/dissolved — walked to the nearest active
      // ancestor. Landing there, but the user is told (§7.9.9).
      return { route: `/communities/${landed}`, stalePrefGuid: prefTrim };
    }
    stalePrefGuid = prefTrim; // gone entirely — fall through, remember why
  }

  // 2. The inviting organization.
  const invited = landFor(invitedCommunityGuid);
  if (invited) return { route: `/communities/${invited}`, stalePrefGuid };

  // 3. Home geo (already walked by the caller; re-validate defensively).
  const home = landFor(homeCommunityGuid);
  if (home) return { route: `/communities/${home}`, stalePrefGuid };

  // Terminal: the browse page — never a crash, never a blank pane.
  return { route: COMMUNITIES_BROWSE_ROUTE, stalePrefGuid };
}

/** Back-compat wrapper (route only) — existing callers/tests keep working. */
export function resolveDefaultCommunityLanding(
  inputs: DefaultCommunityLandingInputs,
): string {
  return resolveStartPage(inputs).route;
}
